import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { toast } from "react-toastify";
import { AppConfig } from "../AppConfig";
import {
    CreateWorkOrderRequest,
    DEFAULT_FDV_CLASS_CODE,
    DocumentUploadRequest,
    GetWorkOrdersParams,
    SyncFeatureToFDVRequest,
    WorkOrdersLightParameterSyncResult,
    WorkPaginatedResult,
} from "../types/workOrderTypes";
import { applyEdits, getFeatureFromGlobalId } from "./arcgisUtils";
import { getAGEToken } from "./authenticate";
import { convertBlobToBase64, dispatchRefreshFeature } from "./helpers";
import { isInvalidFDVSync } from "./fdvSyncGuard";
import { getNFSStatusName, NFSStatus } from "./nfsUtils";
import type Graphic from "@arcgis/core/Graphic";

export async function getWorkOrdersForObject({
    page = 1,
    pageSize = 20,
    assignedTo,
    gisObjId,
    statusCodes,
    classCodes,
}: GetWorkOrdersParams): Promise<WorkPaginatedResult> {
    const baseUrl = AppConfig.NFSAPI.BaseApiUrl + "work/search";
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());

    if (assignedTo) params.append("assignedTo", assignedTo);
    if (gisObjId) params.append("gisObjId", gisObjId.toString());
    if (statusCodes) params.append("statusCodes", statusCodes);
    if (classCodes) params.append("classCodes", classCodes);

    const url = `${baseUrl}?${params.toString()}`;
    const token = await getAGEToken();

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail);
        }

        const data: WorkPaginatedResult = await response.json();
        return data;
    } catch (error) {
        console.error("Error fetching work orders:", error);
        throw error;
    }
}

/**
 * Creates a work order in FDV
 * @param request The work order request data
 * @returns The ID of the created work order
 */
export async function createWorkOrder(request: CreateWorkOrderRequest, typeCode: string): Promise<string | undefined> {
    const url = AppConfig.NFSAPI.BaseApiUrl + "work";
    const token = await getAGEToken();

    request.statusCode = request.statusCode ?? "RPL";
    request.typeCode = typeCode;
    request.classCode = request.classCode ?? DEFAULT_FDV_CLASS_CODE;

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail);
        }

        // The API returns the work order ID as a string
        const workOrderId: string = await response.json();
        return workOrderId;
    } catch (error) {
        console.error("Error creating work order:", error);
        throw error;
    }
}

export async function getObjectEquipmentNo(gis_obj_id: string): Promise<string | undefined> {
    const url = AppConfig.NFSAPI.BaseApiUrl + "fdvadmin/equipmentcode/{gisobjid}".replace("{gisobjid}", gis_obj_id);
    const token = await getAGEToken();

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // The API returns the equipment number as a string
        const equipmentNo: string = await response.json();
        return equipmentNo;
    } catch (error) {
        toast.error("Kunne ikke finne FDV utstyrnummer");
        throw error;
    }
}

export const updateFDVWorkOrdersWithLightParams = async (
    fdvWorkOrderIds: string[],
    nfsLayerId: number,
    nfsGlobalId: string,
    nfsGdbVersion: string | null | undefined
): Promise<boolean> => {
    const url = AppConfig.NFSAPI.BaseApiUrl + "work/updateLightParameters";
    const token = await getAGEToken();

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify({
                workOrderIds: fdvWorkOrderIds,
                layerId: nfsLayerId,
                globalId: nfsGlobalId.replace("{", "").replace("}", ""),
                gdbVersion: nfsGdbVersion,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}}`);
        }

        const responseJson = await response.json();
        const result = responseJson as WorkOrdersLightParameterSyncResult;

        const failedWorkOrders = Object.entries(result.resultsByWorkOrderId).filter(([, res]) => !res.success);

        if (result.featureNotFound || failedWorkOrders.length > 0) {
            console.error("Failed to update light parameters in FDV some work orders: ", failedWorkOrders);
            console.error(result);
            return false;
        } else {
            console.log("Successfully updated light parameters in FDV");
            return true;
        }
    } catch (error) {
        console.error(error);
        return false;
    }
};

export const updateFDVObject = async (
    request: SyncFeatureToFDVRequest,
    selectedFeature?: Graphic | null | undefined,
    setSelectedFeature?: (feature: Graphic) => void,
    shownFeature?: Graphic | null | undefined,
    setShownFeature?: (feature: Graphic) => void
) => {
    // List of valid layer IDs that can be synced
    const validLayerIds = [0, 1, 4, 5, 6, 7, 8, 9, 10, 11, 15];
    if (request.layerId === undefined || request.layerId === null) {
        console.warn("Layer ID is undefined or null in FDV sync request.");
        return;
    }
    if (!validLayerIds.includes(request.layerId)) {
        console.warn(`Layer ID ${request.layerId} is not valid for FDV sync.`);
        return;
    }

    const cleanGlobalId = request.globalId?.replace("{", "").replace("}", "");

    const url =
        AppConfig.NFSAPI.BaseApiUrl + `fdvsync/${request.layerId}/${cleanGlobalId}?gdbVersion=${request.gdbVersion}`;
    const token = await getAGEToken();

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseJson = await response.json();

        if (responseJson.success) {
            if (request.layerId === undefined || request.layerId === null || !request.globalId) return responseJson;

            const feature = await getFeatureFromGlobalId(request.layerId, `{${request.globalId}}`);
            if (!feature) return responseJson;

            feature.attributes.gis_obj_id = responseJson.gisObjId;
            await applyEdits(feature.layer as FeatureLayer, { updateFeatures: [feature] });

            if (selectedFeature?.attributes.globalId === request.globalId && setSelectedFeature) {
                setSelectedFeature(feature.clone());
            }
            if (shownFeature?.attributes.globalId === request.globalId && setShownFeature) {
                setShownFeature(feature.clone());
            }
            return { json: responseJson, feature: feature, success: true };
        } else {
            return { json: responseJson, feature: null, success: false };
        }
    } catch (error) {
        console.error(error);
        toast.error("Kunne ikke synkronisere til FDV");
        // throw error;
    }
};

const DEFAULT_GDB_VERSION = "sde.DEFAULT";

const queryNfsStatus = async (layerId: number, globalId: string, gdbVersion: string): Promise<number | null> => {
    const layer = new FeatureLayer({
        url: `${AppConfig.NauticalObjectsFeatureServer}${layerId}`,
        gdbVersion,
    });
    const result = await layer.queryFeatures({
        where: `globalid = '{${globalId}}'`,
        outFields: ["nfs_status"],
        returnGeometry: false,
    });
    return result.features[0]?.attributes.nfs_status ?? null;
};

/**
 * Checks that the version isn't outdated compared to DEFAULT, so an old projected state is never
 * pushed to FDV (see isInvalidFDVSync). Fails closed: if the statuses can't be read, the sync is not allowed.
 */
const isFDVSyncAllowed = async (request: SyncFeatureToFDVRequest, cleanGlobalId: string): Promise<boolean> => {
    if (request.layerId === undefined || request.layerId === null || !request.gdbVersion) {
        return true;
    }
    if (request.gdbVersion.toLowerCase() === DEFAULT_GDB_VERSION.toLowerCase()) {
        return true;
    }
    try {
        const [versionStatus, defaultStatus] = await Promise.all([
            queryNfsStatus(request.layerId, cleanGlobalId, request.gdbVersion),
            queryNfsStatus(request.layerId, cleanGlobalId, DEFAULT_GDB_VERSION),
        ]);
        if (isInvalidFDVSync(versionStatus, defaultStatus)) {
            toast.error(
                `Objektet har status ${getNFSStatusName(defaultStatus as NFSStatus)} i Planroot, men er prosjektert i denne versjonen. Versjonen er utdatert og objektet kan ikke synkroniseres til FDV. Frisk opp versjonen og prøv igjen.`
            );
            return false;
        }
        return true;
    } catch (error) {
        console.error("Could not verify object status before FDV sync", error);
        toast.error("Kunne ikke kontrollere objektets status i Planroot. Synkronisering til FDV er avbrutt.");
        return false;
    }
};

/**
 * Use when creating a new job and immediately adding objects to it
 * Note - Does applyEdits to add gis_obj_id to feature if not present this can cause lock issues if called multiple times in quick succession
 * @param request
 * @param feature
 * @param allowWithoutFdvObject Skip the DEFAULT status check for objects that don't have an FDV object yet
 * (no gis_obj_id). Used by the job flow, which has always created FDV objects for old objects that lack one.
 * @returns
 */
export const updateFDVObjectNew = async (
    request: SyncFeatureToFDVRequest,
    feature: Graphic,
    allowWithoutFdvObject: boolean = false
): Promise<{ json: unknown; feature: Graphic | null; success: boolean }> => {
    const cleanGlobalId = request.globalId?.replace("{", "").replace("}", "");

    const skipDefaultCheck = allowWithoutFdvObject && !feature.attributes.gis_obj_id;
    if (cleanGlobalId && !skipDefaultCheck && !(await isFDVSyncAllowed(request, cleanGlobalId))) {
        return { json: null, feature: null, success: false };
    }

    const url =
        AppConfig.NFSAPI.BaseApiUrl + `fdvsync/${request.layerId}/${cleanGlobalId}?gdbVersion=${request.gdbVersion}`;
    const token = await getAGEToken();

    const toastId = "fdv-sync";
    toast.info("Synkroniserer objekt til FDV...", { toastId, autoClose: false });
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseJson = await response.json();

        if (responseJson.success) {
            if (request.layerId === undefined || request.layerId === null || !request.globalId) return responseJson;

            if (!feature.attributes.gis_obj_id) {
                feature.attributes.gis_obj_id = responseJson.gisObjId;
                await applyEdits(feature.layer as FeatureLayer, { updateFeatures: [feature] });
                const layerid = (feature.layer as FeatureLayer)?.layerId;
                const objectId = feature.getObjectId();
                if (layerid !== undefined && objectId !== undefined && layerid !== null && objectId !== null) {
                    dispatchRefreshFeature(layerid.toString(), objectId as number, false);
                }
            }

            return { json: responseJson, feature: feature, success: true };
        } else {
            return { json: responseJson, feature: null, success: false };
        }
    } catch (error) {
        console.error(error);
        toast.error("Kunne ikke synkronisere til FDV");
        return { json: null, feature: null, success: false };
    } finally {
        toast.update(toastId, {
            render: `Synkronisering til FDV fullført for ${feature.attributes.sjomerke_nr ?? "mangler sjømerkenummer"}`,
            type: "success",
            autoClose: 3000,
        });
    }
};

export const openInFDV = async (GISOBJID: string) => {
    const equipmentNo = await getObjectEquipmentNo(GISOBJID);
    window.open(`${AppConfig.FDV.BaseUrl}${AppConfig.FDV.ObjectPropertiesBase}${equipmentNo}`, "_blank");
};

export const uploadFileToFDV = async (
    workOrderIds: string[],
    fileBlob: Blob,
    filename: string,
    description: string
): Promise<boolean> => {
    const url = `${AppConfig.NFSAPI.BaseApiUrl}fdvadmin/documents`;
    const token = await getAGEToken();
    const base64stringWithPrefix = await convertBlobToBase64(fileBlob);
    const uploadResults: Record<string, boolean> = {};
    if (!base64stringWithPrefix) {
        return false;
    }
    // Remove the data URL prefix (e.g., "data:text/csv;charset=utf-8;base64,")
    const base64string = base64stringWithPrefix.split(",")[1];
    for (const workOrderId of workOrderIds) {
        const documentUploadRequest: DocumentUploadRequest = {
            attachToWorkOrderId: workOrderId,
            fileName: filename,
            description: description,
            base64Content: base64string,
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(documentUploadRequest),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}}`);
            }
        } catch (error) {
            console.error("Error uploading document to FDV:", error);
            uploadResults[workOrderId] = false;
            toast.error(`Feil ved opplasting av ${filename} til arbeidsordre ${workOrderId}`);
            continue;
        }
        uploadResults[workOrderId] = true;
    }

    // Return true if all uploads were successful
    return Object.values(uploadResults).every((result) => result === true);
};
